import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, PlayCircle } from "lucide-react";

/**
 * Vídeo de ajuda de uma "primeira vez" (Configurações → Vídeos de ajuda).
 * Abre sozinho na 1ª vez, fecha quando o vídeo termina e deixa o botão "Ver vídeo" no lugar.
 * Sem vídeo escolhido para o momento, não mostra nada.
 *   moment: primeiro_acesso | contrato | primeira_aula | prova | certificado
 *   preview: admin vendo a tela (abre sempre e não marca como visto)
 */
export function HelpVideo({ moment, label = "Ver vídeo de ajuda", title = "Veja como funciona", preview = false, className = "" }) {
  const [help, setHelp] = useState(null);       // { url, seen }
  const [open, setOpen] = useState(false);
  const [semSom, setSemSom] = useState(false);
  const vid = useRef(null);

  useEffect(() => {
    let vivo = true;
    api.get(`/me/help/${moment}`).then((r) => {
      if (!vivo) return;
      setHelp(r.data);
      if (r.data.url && (preview || !r.data.seen)) setOpen(true);
    }).catch(() => {});
    return () => { vivo = false; };
  }, [moment, preview]);

  // começa sozinho: tenta com som; se o navegador bloquear, toca mudo com o botão "Toque para ouvir"
  useEffect(() => {
    if (!open) return;
    const v = vid.current;
    if (!v) return;
    setSemSom(false);
    v.muted = false;
    v.play().catch(() => { v.muted = true; setSemSom(true); v.play().catch(() => {}); });
  }, [open]);

  if (!help?.url) return null;

  const fechar = () => {
    setOpen(false);
    if (!help.seen && !preview) { setHelp((h) => ({ ...h, seen: true })); api.post(`/me/help/${moment}/seen`).catch(() => {}); }
  };
  const ligarSom = () => { const v = vid.current; if (!v) return; v.muted = false; v.currentTime = 0; v.play().catch(() => {}); setSemSom(false); };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`help-video-${moment}`}
        className={`inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E9E2A] hover:underline ${className}`}>
        <PlayCircle className="h-4 w-4" /> {label}
      </button>
      {open && (
        <div className="fixed inset-0 z-[300] bg-black/75 grid place-items-center p-3 sm:p-6" onClick={fechar} data-testid={`help-modal-${moment}`}>
          <div className="relative w-full max-w-sm sm:max-w-md bg-black rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-[#232D4F] text-white">
              <span className="text-sm font-semibold">{title}</span>
              <button type="button" onClick={fechar} className="p-1 rounded-full hover:bg-white/15" title="Fechar"><X className="h-5 w-5" /></button>
            </div>
            <div className="relative">
              <video ref={vid} src={help.url} controls playsInline controlsList="nodownload" disablePictureInPicture
                className="w-full max-h-[78vh] bg-black" onEnded={fechar} onContextMenu={(e) => e.preventDefault()}
                onVolumeChange={(e) => { if (!e.currentTarget.muted) setSemSom(false); }} />
              {semSom && (
                <button type="button" onClick={ligarSom} className="absolute inset-x-0 top-1/3 mx-auto w-fit flex items-center gap-2 rounded-full bg-white/95 px-5 py-3 text-base font-bold text-[#232D4F] shadow-xl animate-pulse">
                  🔊 Toque para ouvir
                </button>
              )}
            </div>
            <button type="button" onClick={fechar} className="w-full py-3 text-sm font-semibold text-white bg-[#1E9E2A] hover:bg-green-700">Já entendi, continuar</button>
          </div>
        </div>
      )}
    </>
  );
}
